// Context Cards — the library.
//
// Context cards are standing research entries: a person, a treaty, an
// institution, an operation. They are written once and reused across every
// article that needs the background, which is what makes this page necessary —
// a card attached to nothing at all is otherwise invisible, and a published
// card with an error in it is replicated everywhere it appears with no way to
// reach it.
//
// Endpoints:
//   GET   /news/editor/context-cards?status=&search=&limit=&offset=
//   POST  /news/editor/context-cards                  — create
//   PATCH /news/editor/context-cards/:cardId          — edit (no status gate)
//
// The list endpoint filters to exactly ONE status per call and defaults to
// published, so the tabs below are the whole of the filtering — there is
// deliberately no "all" view to build.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

export interface ContextCardRow {
  id: string;
  slug: string;
  category: string;
  title: string;
  body: string;
  source_notes: string | null;
  external_url: string | null;
  external_label: string | null;
  key_entities: string[];
  status: "draft" | "published" | "archived";
  /** The one entity the card is chiefly about; supplies the image fallback. */
  primary_entity_id: string | null;
  primary_entity: {
    id: string;
    name: string;
    entity_type: string;
    image_url: string | null;
  } | null;
  image_url: string | null;
  image_credit: string | null;
  image_license: string | null;
  /** Resolved server-side: the card's own image, else the subject's. */
  image: {
    url: string;
    credit: string | null;
    license: string | null;
    from: "card" | "entity";
  } | null;
  created_at: string;
  updated_at: string;
}

type Status = "published" | "draft" | "archived";

const TABS: Array<{ key: Status; label: string }> = [
  { key: "published", label: "Published" },
  { key: "draft", label: "Drafts" },
  { key: "archived", label: "Archived" },
];

const PAGE = 25;

export default function ContextCardsPage() {
  const [status, setStatus] = useState<Status>("published");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<ContextCardRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Debounced so a search does not fire a request per keystroke.
  const [debounced, setDebounced] = useState("");
  const firstRun = useRef(true);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to the filters invalidates the current page number.
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setOffset(0);
  }, [status, debounced]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const q = new URLSearchParams({
        status,
        limit: String(PAGE),
        offset: String(offset),
      });
      if (debounced) q.set("search", debounced);
      const r = await api<{ cards: ContextCardRow[]; total: number }>(
        `/news/editor/context-cards?${q}`,
      );
      setRows(r.cards);
      setTotal(r.total);
      setError(null);
    } catch (e: any) {
      setError(describeApiError(e));
      setRows([]);
    }
  }, [status, debounced, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const showing = useMemo(() => {
    if (!rows?.length) return null;
    return `${offset + 1}–${offset + rows.length} of ${total}`;
  }, [rows, offset, total]);

  return (
    <div>
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Context Cards</h1>
          <CardSubtle style={{ margin: "4px 0 0" }}>
            Reusable background on a person, agreement, institution or event.
            One card, many articles.
          </CardSubtle>
        </div>
        <Link to="/context-cards/new">
          <Button>New card</Button>
        </Link>
      </header>

      <div style={styles.controls}>
        <div style={styles.tabBar}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              style={{
                ...styles.tab,
                ...(status === t.key ? styles.tabActive : null),
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title or category…"
          style={{ maxWidth: 320 }}
        />
      </div>

      {error && (
        <CardSubtle style={{ color: "var(--danger)", marginBottom: 10 }}>
          {error}
        </CardSubtle>
      )}

      {!rows ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <Card padding={28}>
          <CardSubtle>
            {debounced
              ? `Nothing ${status} matches “${debounced}”.`
              : status === "published"
                ? "No published cards yet. Draft one and publish it when it reads right."
                : `No ${status} cards.`}
          </CardSubtle>
        </Card>
      ) : (
        <>
          <div style={styles.list}>
            {rows.map((c) => (
              <Link key={c.id} to={`/context-cards/${c.id}`} style={styles.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowTitle}>{c.title}</div>
                  <div style={styles.rowMeta}>
                    <span style={styles.category}>{c.category}</span>
                    {c.key_entities.length > 0 && (
                      <span style={{ color: "var(--text-muted)" }}>
                        {c.key_entities.slice(0, 4).join(" · ")}
                        {c.key_entities.length > 4 &&
                          ` +${c.key_entities.length - 4}`}
                      </span>
                    )}
                  </div>
                  <div style={styles.excerpt}>{excerpt(c.body)}</div>
                </div>
                <div style={styles.rowRight}>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {formatDate(c.updated_at)}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--accent)" }}>
                    Edit →
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div style={styles.pager}>
            <CardSubtle style={{ margin: 0 }}>{showing}</CardSubtle>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="secondary"
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
                disabled={offset === 0}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                onClick={() => setOffset(offset + PAGE)}
                disabled={offset + rows.length >= total}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The API returns zod's fieldErrors as `error`, and lib/api passes that object
 * in as the Error MESSAGE — so rendering err.message on a 400 prints
 * "[object Object]". Read the structured body and name the field instead.
 */
export function describeApiError(err: any): string {
  const detail = err?.data?.error ?? err?.message;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const parts = Object.entries(detail).map(
      ([field, msgs]) =>
        `${field.replace(/_/g, " ")}: ${
          Array.isArray(msgs) ? msgs.join(", ") : String(msgs)
        }`,
    );
    if (parts.length) return parts.join(" · ");
  }
  return "Something went wrong";
}

/** First line of the markdown body, stripped of the lightest formatting. */
function excerpt(body: string): string {
  const flat = body
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return flat.length > 180 ? `${flat.slice(0, 180)}…` : flat;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 20,
  },
  h1: { fontSize: 22, fontWeight: 700, margin: 0 },
  controls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  tabBar: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid var(--border)",
  },
  tab: {
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "8px 12px",
    font: "inherit",
    fontSize: 14,
    color: "var(--text-muted)",
    cursor: "pointer",
  },
  tabActive: {
    color: "var(--text-primary)",
    fontWeight: 600,
    borderBottomColor: "var(--accent)",
  },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: "14px 16px",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    textDecoration: "none",
    color: "inherit",
  },
  rowTitle: { fontWeight: 600, fontSize: 15, marginBottom: 3 },
  rowMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    marginBottom: 6,
    flexWrap: "wrap",
  },
  category: {
    color: "var(--accent)",
    background: "var(--surface-hover)",
    borderRadius: 3,
    padding: "1px 6px",
  },
  excerpt: {
    fontSize: 13,
    color: "var(--text-secondary)",
    lineHeight: 1.5,
  },
  rowRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
    whiteSpace: "nowrap",
  },
  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 16,
  },
};
